import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { GEN } from "../lib/api";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = isRegister
      ? await register(name, email, password)
      : await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(isRegister ? "Welcome to the Universe!" : "Welcome back, hero!");
      navigate("/");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={GEN.closeup} alt="NAIJAMAN" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-[#050806]" />
        <div className="absolute bottom-12 left-12">
          <h2 className="comic-title text-6xl">NAIJA<span className="text-amber-500">MAN</span></h2>
          <p className="text-gray-300 mt-2 max-w-xs">Join the universe. Unlock every issue.</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-[#050806] relative">
        <div className="halftone absolute inset-0 opacity-20" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative w-full max-w-md"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-emerald-400 mb-8 font-accent tracking-wider" data-testid="auth-back-link">
            <ArrowLeft size={16} /> BACK TO SITE
          </Link>
          <h1 className="font-display text-5xl text-white">{isRegister ? "JOIN THE UNIVERSE" : "WELCOME BACK"}</h1>
          <p className="text-gray-400 mt-2">{isRegister ? "Create your free hero account." : "Sign in to read the full saga."}</p>

          {error && <div className="mt-5 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-md px-4 py-3" data-testid="auth-error">{error}</div>}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {isRegister && (
              <input
                value={name} onChange={(e) => setName(e.target.value)} required placeholder="Name"
                className="w-full bg-white/5 border border-white/15 rounded-md px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400"
                data-testid="auth-name-input"
              />
            )}
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email"
              className="w-full bg-white/5 border border-white/15 rounded-md px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400"
              data-testid="auth-email-input"
            />
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password"
              className="w-full bg-white/5 border border-white/15 rounded-md px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400"
              data-testid="auth-password-input"
            />
            <button
              type="submit" disabled={loading}
              className="w-full btn-skew bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-accent text-xl tracking-wider py-3 glow-green disabled:opacity-60"
              data-testid="auth-submit-btn"
            >
              <span>{loading ? "Please wait..." : isRegister ? "Create Account" : "Sign In"}</span>
            </button>
          </form>

          <p className="text-gray-400 mt-6 text-center">
            {isRegister ? "Already a member? " : "New to the universe? "}
            <Link to={isRegister ? "/login" : "/register"} className="text-emerald-400 hover:text-emerald-300 font-semibold" data-testid="auth-switch-link">
              {isRegister ? "Sign In" : "Create Account"}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
